export const PVC_COCKPIT_LANGUAGES = ['pt-BR', 'en', 'es', 'fr'] as const;

export type PvcCockpitLanguage = (typeof PVC_COCKPIT_LANGUAGES)[number];

type PvcCockpitCopy = {
  title: string;
  subtitle: string;
  savings: string;
  savingsSubtitle: string;
  statsTitle: string;
  statsSales: string;
  statsSalesLabel: string;
  statsViews: string;
  statsViewsLabel: string;
  statsReach: string;
  statsReachLabel: string;
  galleryTitle: string;
  gallerySubtitle: string;
  galleryAlts: string[];
  learnTitle: string;
  learn1: string;
  learn2: string;
  learn3: string;
  learn4: string;
  learn5: string;
  whyPvcTitle: string;
  benefit1Title: string;
  benefit1Desc: string;
  benefit2Title: string;
  benefit2Desc: string;
  benefit3Title: string;
  benefit3Desc: string;
  benefit4Title: string;
  benefit4Desc: string;
  youtubeTitle: string;
  youtubeDescription: string;
  youtubeLink: string;
  testimonial: string;
  testimonialAuthor: string;
  priceFrom: string;
  price: string;
  priceNote: string;
  securePayment: string;
  instantAccess: string;
  faqTitle: string;
  faq1Question: string;
  faq1Answer: string;
  faq2Question: string;
  faq2Answer: string;
  faq3Question: string;
  faq3Answer: string;
  bylineTitle: string;
  bylineCta: string;
};

export const PVC_COCKPIT_METRICS = {
  salesCount: 500,
  youtubeViews: 100_000,
  socialReach: 5_000_000,
} as const;

type PvcCockpitSaleData = {
  hotmart: {
    checkoutUrl: string;
    price: string;
    priceCurrency: string;
  };
  youtube: {
    videoId: string;
  };
  gallery: {
    basePath: string;
    imageCount: number;
  };
  copyByLang: Record<PvcCockpitLanguage, PvcCockpitCopy>;
  buttonTextByLang: Record<PvcCockpitLanguage, string>;
};

export const PVC_COCKPIT_SALE: PvcCockpitSaleData = {
  hotmart: {
    checkoutUrl: 'https://pay.hotmart.com/A103723982V?checkoutMode=2',
    // Structured price for JSON-LD; matches the en copy shown on the page.
    // Hotmart localizes the charged amount per region at checkout.
    price: '7.00',
    priceCurrency: 'USD',
  },
  youtube: {
    videoId: 'g-YxT9fY9us',
  },
  gallery: {
    basePath: '/images/pvc-cockpit',
    imageCount: 11,
  },
  buttonTextByLang: {
    'pt-BR': 'Comprar Agora',
    en: 'Buy Now',
    es: 'Comprar Ahora',
    fr: 'Acheter Maintenant',
  },
  copyByLang: {
    'pt-BR': {
      title: '🏎️ Construa seu Cockpit de PVC',
      subtitle: 'O guia definitivo para criar seu simulador de corrida em casa',
      savings: '💰 Economize até 80%',
      savingsSubtitle: 'Comparado a cockpits comerciais de alumínio',
      statsTitle: '🏆 Aprovado pela comunidade',
      statsSales: '500+',
      statsSalesLabel: 'PDFs vendidos',
      statsViews: '100 mil+',
      statsViewsLabel: 'Visualizações no YouTube',
      statsReach: '5 mi+',
      statsReachLabel: 'Alcance nas redes sociais',
      galleryTitle: '📸 Veja o resultado final',
      gallerySubtitle: 'Clique nas imagens para ampliar',
      galleryAlts: [
        'Prévia do guia: página de fotos com seis ângulos do cockpit de PVC montado com monitor, volante e assento de tecido',
        'Prévia do guia: passos de montagem com renders 3D mostrando como cada cano e conexão de PVC se encaixa',
        'Prévia do guia: página de observações sobre parafusar as bases de madeira e conexões, com fotos em close',
        'Vista lateral do cockpit de PVC preto finalizado com assento de correias trançadas e suporte do volante',
        'Vista frontal em diagonal do cockpit com volante Logitech G29 e pedais instalados',
        'Close do suporte do câmbio com uma placa Arduino personalizada instalada atrás da base do volante',
        'Vista traseira da estrutura do cockpit de PVC preto mostrando o assento e o suporte do monitor',
        'Vista frontal do cockpit mostrando a base dos pedais, a base do volante e o assento de correias',
        'Visão do piloto: volante Logitech G29 montado no cockpit em frente ao PC gamer',
        'O cockpit de PVC montado em frente à mesa com monitor ultrawide e PC gamer',
        'Vista ampla do cockpit finalizado e do setup completo de sim racing na mesa',
      ],
      learnTitle: '✅ O que você vai aprender:',
      learn1: 'Lista completa de materiais com medidas exatas em PVC',
      learn2: 'Instruções passo a passo com fotos detalhadas de montagem',
      learn3: 'Dicas de ergonomia para posição ideal de pilotagem',
      learn4: 'Compatível com Logitech G29, G920, Thrustmaster e mais',
      learn5: 'Estrutura robusta que aguenta horas de uso intenso',
      whyPvcTitle: '🔧 Por que PVC?',
      benefit1Title: 'Custo-benefício',
      benefit1Desc: 'Materiais encontrados em qualquer loja de construção',
      benefit2Title: 'Fácil de montar',
      benefit2Desc: 'Sem soldas ou ferramentas especiais',
      benefit3Title: 'Personalizável',
      benefit3Desc: 'Ajuste as medidas para seu espaço e altura',
      benefit4Title: 'Desmontável',
      benefit4Desc: 'Guarde facilmente quando não estiver usando',
      youtubeTitle: '🎥 O mais popular da comunidade',
      youtubeDescription:
        'Mais de 100 mil visualizações! O cockpit DIY de PVC mais assistido e aprovado pela comunidade de sim racing.',
      youtubeLink: 'Assistir no YouTube',
      testimonial:
        'Montei o meu em um final de semana e já estou correndo no iRacing com uma experiência completamente diferente!',
      testimonialAuthor: '- Entusiasta de sim racing',
      priceFrom: 'De R$ 97,00',
      price: 'R$ 29,96',
      priceNote: 'Acesso imediato ao PDF completo',
      securePayment: '🔒 Pagamento 100% seguro via Hotmart',
      instantAccess: 'Acesso imediato após a confirmação do pagamento',
      faqTitle: '❓ Perguntas Frequentes',
      faq1Question: 'Quanto vou gastar nos materiais?',
      faq1Answer:
        'Em média, entre R$ 200 a R$ 350 dependendo da sua região e opções escolhidas.',
      faq2Question: 'Preciso de ferramentas especiais?',
      faq2Answer:
        'Não! Apenas um serrote comum para cortar os canos. Algumas lojas até cortam para você.',
      faq3Question: 'Funciona com qualquer volante?',
      faq3Answer:
        'Sim, o tutorial inclui adaptações para os modelos mais populares do mercado.',
      bylineTitle: 'Criado por Victor Saisse',
      bylineCta: 'Veja o resto do meu trabalho',
    },
    en: {
      title: '🏎️ Build Your PVC Cockpit',
      subtitle: 'The ultimate guide to create your racing simulator at home',
      savings: '💰 Save up to 80%',
      savingsSubtitle: 'Compared to commercial aluminum cockpits',
      statsTitle: '🏆 Community-approved',
      statsSales: '500+',
      statsSalesLabel: 'PDFs sold',
      statsViews: '100k+',
      statsViewsLabel: 'YouTube views',
      statsReach: '5M+',
      statsReachLabel: 'Social media reach',
      galleryTitle: '📸 See the final result',
      gallerySubtitle: 'Click on images to enlarge',
      galleryAlts: [
        'Guide preview: photo page showing six views of the assembled PVC cockpit with monitor, wheel, and fabric seat',
        'Guide preview: assembly steps with 3D renders showing how each PVC pipe and fitting connects',
        'Guide preview: notes page on screwing down the wooden bases and fittings, with close-up photos',
        'Side view of the finished black PVC cockpit with woven strap seat and steering wheel mount',
        'Front three-quarter view of the cockpit with a Logitech G29 wheel and pedals mounted',
        'Close-up of the shifter mount with a custom Arduino board installed behind the wheel base',
        'Rear view of the black PVC cockpit frame showing the seat and monitor mount structure',
        'Front view of the cockpit showing the pedal plate, wheel deck, and woven strap seat',
        'Driver point of view: Logitech G29 racing wheel mounted on the cockpit in front of the gaming PC',
        'The PVC cockpit set up at a desk with an ultrawide monitor and gaming PC',
        'Wide view of the finished cockpit and full sim racing setup at the desk',
      ],
      learnTitle: '✅ What you will learn:',
      learn1: 'Complete list of materials with exact PVC measurements',
      learn2: 'Step-by-step instructions with detailed assembly photos',
      learn3: 'Ergonomic tips for the ideal driving position',
      learn4: 'Compatible with Logitech G29, G920, Thrustmaster and more',
      learn5: 'Sturdy structure that withstands hours of intensive use',
      whyPvcTitle: '🔧 Why PVC?',
      benefit1Title: 'Cost-effective',
      benefit1Desc: 'Materials found at any hardware store',
      benefit2Title: 'Easy to assemble',
      benefit2Desc: 'No welding or special tools needed',
      benefit3Title: 'Customizable',
      benefit3Desc: 'Adjust measurements to your space and height',
      benefit4Title: 'Portable',
      benefit4Desc: 'Easily store when not in use',
      youtubeTitle: '🎥 Most popular in the community',
      youtubeDescription:
        'Over 100k views! The most watched and approved PVC DIY cockpit in the sim racing community.',
      youtubeLink: 'Watch on YouTube',
      testimonial:
        'I built mine in a weekend and I am already racing in iRacing with a completely different experience!',
      testimonialAuthor: '- Sim racing enthusiast',
      priceFrom: 'From $19.00',
      price: '$7.00',
      priceNote: 'Instant access to the complete PDF',
      securePayment: '🔒 100% secure payment via Hotmart',
      instantAccess: 'Instant access after payment confirmation',
      faqTitle: '❓ Frequently Asked Questions',
      faq1Question: 'How much will I spend on materials?',
      faq1Answer:
        'On average, between $40 to $70 depending on your region and chosen options.',
      faq2Question: 'Do I need special tools?',
      faq2Answer:
        'No! Just a common saw to cut the pipes. Some stores even cut them for you.',
      faq3Question: 'Does it work with any steering wheel?',
      faq3Answer:
        'Yes, the tutorial includes adaptations for the most popular models on the market.',
      bylineTitle: 'Built by Victor Saisse',
      bylineCta: 'See the rest of my work',
    },
    es: {
      title: '🏎️ Construye tu Cockpit de PVC',
      subtitle:
        'La guía definitiva para crear tu simulador de carreras en casa',
      savings: '💰 Ahorra hasta 80%',
      savingsSubtitle: 'Comparado con cockpits comerciales de aluminio',
      statsTitle: '🏆 Aprobado por la comunidad',
      statsSales: '500+',
      statsSalesLabel: 'PDFs vendidos',
      statsViews: '100 mil+',
      statsViewsLabel: 'Visualizaciones en YouTube',
      statsReach: '5 M+',
      statsReachLabel: 'Alcance en redes sociales',
      galleryTitle: '📸 Mira el resultado final',
      gallerySubtitle: 'Haz clic en las imágenes para ampliar',
      galleryAlts: [
        'Vista previa de la guía: página de fotos con seis ángulos del cockpit de PVC montado con monitor, volante y asiento de tela',
        'Vista previa de la guía: pasos de montaje con renders 3D que muestran cómo encaja cada tubo y conexión de PVC',
        'Vista previa de la guía: página de observaciones sobre atornillar las bases de madera y conexiones, con fotos en primer plano',
        'Vista lateral del cockpit de PVC negro terminado con asiento de correas trenzadas y soporte del volante',
        'Vista frontal en diagonal del cockpit con volante Logitech G29 y pedales instalados',
        'Primer plano del soporte del cambio con una placa Arduino personalizada instalada detrás de la base del volante',
        'Vista trasera de la estructura del cockpit de PVC negro mostrando el asiento y el soporte del monitor',
        'Vista frontal del cockpit mostrando la base de los pedales, la base del volante y el asiento de correas',
        'Vista del piloto: volante Logitech G29 montado en el cockpit frente al PC gamer',
        'El cockpit de PVC instalado frente al escritorio con monitor ultrawide y PC gamer',
        'Vista amplia del cockpit terminado y del setup completo de sim racing en el escritorio',
      ],
      learnTitle: '✅ Lo que aprenderás:',
      learn1: 'Lista completa de materiales con medidas exactas en PVC',
      learn2: 'Instrucciones paso a paso con fotos detalladas de montaje',
      learn3: 'Consejos de ergonomía para la posición ideal de conducción',
      learn4: 'Compatible con Logitech G29, G920, Thrustmaster y más',
      learn5: 'Estructura robusta que aguanta horas de uso intenso',
      whyPvcTitle: '🔧 ¿Por qué PVC?',
      benefit1Title: 'Económico',
      benefit1Desc: 'Materiales encontrados en cualquier ferretería',
      benefit2Title: 'Fácil de montar',
      benefit2Desc: 'Sin soldaduras ni herramientas especiales',
      benefit3Title: 'Personalizable',
      benefit3Desc: 'Ajusta las medidas a tu espacio y altura',
      benefit4Title: 'Desmontable',
      benefit4Desc: 'Guárdalo fácilmente cuando no lo uses',
      youtubeTitle: '🎥 El más popular de la comunidad',
      youtubeDescription:
        '¡Más de 100 mil visualizaciones! El cockpit DIY de PVC más visto y aprobado por la comunidad de sim racing.',
      youtubeLink: 'Ver en YouTube',
      testimonial:
        '¡Monté el mío en un fin de semana y ya estoy corriendo en iRacing con una experiencia completamente diferente!',
      testimonialAuthor: '- Entusiasta del sim racing',
      priceFrom: 'De 19,00 €',
      price: '7,28 €',
      priceNote: 'Acceso inmediato al PDF completo',
      securePayment: '🔒 Pago 100% seguro vía Hotmart',
      instantAccess: 'Acceso inmediato después de la confirmación del pago',
      faqTitle: '❓ Preguntas Frecuentes',
      faq1Question: '¿Cuánto gastaré en materiales?',
      faq1Answer:
        'En promedio, entre 40 € y 70 € dependiendo de tu región y opciones elegidas.',
      faq2Question: '¿Necesito herramientas especiales?',
      faq2Answer:
        '¡No! Solo un serrucho común para cortar los tubos. Algunas tiendas incluso los cortan por ti.',
      faq3Question: '¿Funciona con cualquier volante?',
      faq3Answer:
        'Sí, el tutorial incluye adaptaciones para los modelos más populares del mercado.',
      bylineTitle: 'Creado por Victor Saisse',
      bylineCta: 'Mira el resto de mi trabajo',
    },
    fr: {
      title: '🏎️ Construisez votre Cockpit en PVC',
      subtitle:
        'Le guide ultime pour créer votre simulateur de course à la maison',
      savings: "💰 Économisez jusqu'à 80%",
      savingsSubtitle: 'Comparé aux cockpits commerciaux en aluminium',
      statsTitle: '🏆 Approuvé par la communauté',
      statsSales: '500+',
      statsSalesLabel: 'PDF vendus',
      statsViews: '100k+',
      statsViewsLabel: 'Vues sur YouTube',
      statsReach: '5 M+',
      statsReachLabel: 'Portée sur les réseaux sociaux',
      galleryTitle: '📸 Voir le résultat final',
      gallerySubtitle: 'Cliquez sur les images pour agrandir',
      galleryAlts: [
        'Aperçu du guide : page de photos avec six angles du cockpit en PVC assemblé avec écran, volant et siège en tissu',
        "Aperçu du guide : étapes d'assemblage avec rendus 3D montrant comment chaque tuyau et raccord en PVC s'emboîte",
        'Aperçu du guide : page de remarques sur le vissage des bases en bois et des raccords, avec photos en gros plan',
        'Vue latérale du cockpit en PVC noir terminé avec siège en sangles tressées et support de volant',
        'Vue de trois quarts avant du cockpit avec volant Logitech G29 et pédalier installés',
        'Gros plan du support du levier de vitesse avec une carte Arduino personnalisée installée derrière la base du volant',
        "Vue arrière de la structure du cockpit en PVC noir montrant le siège et le support d'écran",
        'Vue avant du cockpit montrant la base du pédalier, la base du volant et le siège en sangles',
        'Vue du pilote : volant Logitech G29 monté sur le cockpit devant le PC gamer',
        'Le cockpit en PVC installé devant le bureau avec écran ultrawide et PC gamer',
        'Vue large du cockpit terminé et du setup complet de sim racing au bureau',
      ],
      learnTitle: '✅ Ce que vous apprendrez :',
      learn1: 'Liste complète des matériaux avec mesures exactes en PVC',
      learn2:
        "Instructions étape par étape avec photos détaillées d'assemblage",
      learn3: 'Conseils ergonomiques pour la position de conduite idéale',
      learn4: 'Compatible avec Logitech G29, G920, Thrustmaster et plus',
      learn5:
        "Structure robuste qui résiste à des heures d'utilisation intensive",
      whyPvcTitle: '🔧 Pourquoi le PVC ?',
      benefit1Title: 'Économique',
      benefit1Desc: 'Matériaux trouvés dans tout magasin de bricolage',
      benefit2Title: 'Facile à assembler',
      benefit2Desc: 'Sans soudure ni outils spéciaux',
      benefit3Title: 'Personnalisable',
      benefit3Desc: 'Ajustez les mesures à votre espace et taille',
      benefit4Title: 'Démontable',
      benefit4Desc: "Rangez-le facilement quand vous ne l'utilisez pas",
      youtubeTitle: '🎥 Le plus populaire de la communauté',
      youtubeDescription:
        'Plus de 100 000 vues ! Le cockpit DIY en PVC le plus regardé et approuvé par la communauté de sim racing.',
      youtubeLink: 'Regarder sur YouTube',
      testimonial:
        "J'ai construit le mien en un week-end et je cours déjà sur iRacing avec une expérience complètement différente !",
      testimonialAuthor: '- Passionné de sim racing',
      priceFrom: 'De 17,00 €',
      price: '7,39 €',
      priceNote: 'Accès immédiat au PDF complet',
      securePayment: '🔒 Paiement 100% sécurisé via Hotmart',
      instantAccess: 'Accès immédiat après confirmation du paiement',
      faqTitle: '❓ Questions Fréquentes',
      faq1Question: 'Combien vais-je dépenser en matériaux ?',
      faq1Answer:
        'En moyenne, entre 35€ et 60€ selon votre région et les options choisies.',
      faq2Question: "Ai-je besoin d'outils spéciaux ?",
      faq2Answer:
        'Non ! Juste une scie commune pour couper les tuyaux. Certains magasins les coupent même pour vous.',
      faq3Question: 'Est-ce compatible avec tous les volants ?',
      faq3Answer:
        'Oui, le tutoriel inclut des adaptations pour les modèles les plus populaires du marché.',
      bylineTitle: 'Créé par Victor Saisse',
      bylineCta: 'Découvrez le reste de mon travail',
    },
  },
};

export const PVC_COCKPIT_LANG_STORAGE_KEY = 'pvc-cockpit-lang';

/**
 * The page server-renders English by default (the canonical URL is
 * language-neutral); only returning visitors with a stored preference
 * get switched after hydration.
 */
export function getStoredPvcCockpitLanguage(): PvcCockpitLanguage | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = window.localStorage.getItem(PVC_COCKPIT_LANG_STORAGE_KEY);
    return stored &&
      (PVC_COCKPIT_LANGUAGES as readonly string[]).includes(stored)
      ? (stored as PvcCockpitLanguage)
      : null;
  } catch {
    return null;
  }
}

export function storePvcCockpitLanguage(lang: PvcCockpitLanguage): void {
  try {
    window.localStorage.setItem(PVC_COCKPIT_LANG_STORAGE_KEY, lang);
  } catch {
    // Storage unavailable (private mode); language still switches for the session.
  }
}
